import React, { lazy, Suspense, useState, useCallback } from "react";
import { Popup, Button } from "../Base";
import { useT } from "../../i18n/hooks";
import { UnpaidOrderIcon } from "../Icon/IconSvgMap";
import type { UnpaidGuideModalProps } from "./UnpaidGuideModal.types";
import "../../pages/UnpaidOrders/UnpaidOrders.scss";
import "./UnpaidGuideModal.scss";

const UploadProofSectionB = lazy(
  () => import("../../pages/UnpaidOrders/components/UploadProofSectionB")
);

function formatCreatedDate(created?: number | string): string {
  if (!created) return "";
  const d = typeof created === "number" ? new Date(created) : new Date(created);
  if (isNaN(d.getTime())) return String(created);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export const UnpaidGuideModal: React.FC<UnpaidGuideModalProps> = ({
  visible,
  order,
  onClose,
  onConfirmed,
}) => {
  const t = useT();
  const [uploaded, setUploaded] = useState(false);
  console.log("order", order);
  const handleUploadSuccess = useCallback(() => {
    setUploaded(true);
  }, []);

  if (!order) return null;

  const normalized = (order.status || "").toString().toUpperCase();
  const hasVoucher =
    uploaded ||
    (Array.isArray(order.paymentVoucherImgs) &&
      order.paymentVoucherImgs.length > 0);
  const isRejected =
    hasVoucher && normalized === "PENDING" && !!order.rejectTemplateContent;

  return (
    <Popup
      visible={visible}
      onClose={onClose}
      placement="bottom"
      destroyOnClose
      closeOnSwipe={false}
      className="unpaid-guide-popup"
    >
      <div className="unpaid-guide__container">
        <div className="unpaid-guide__header">
          <div className="unpaid-guide__title-bar">
            <span className="unpaid-guide__indicator" />
            <span className="unpaid-guide__title">
              {t("unpaidOrders.guideTitle")}
            </span>
          </div>
          <button
            type="button"
            className="unpaid-guide__close"
            onClick={onClose}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5l10 10"
                stroke="rgba(255,255,255,0.5)"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="unpaid-guide__divider" />

        <div className="unpaid-guide__question">
          <div className="unpaid-guide__icon-circle">
            <UnpaidOrderIcon width={24} height={18} />
          </div>
          <p className="unpaid-guide__subtitle">
            <span className="unpaid-guide__highlight">Transaction ID</span>{" "}
            {t("unpaidOrders.guideSubtitle")}
          </p>
        </div>

        <div className="unpaid-guide__confirm-wrap">
          <Button variant="tertiary" size="lg" block onClick={onConfirmed}>
            {t("unpaidOrders.guideYesEntered")}
          </Button>
        </div>

        <p className="unpaid-guide__no-text">
          {t("unpaidOrders.guideNoPrefix")}
          <span className="unpaid-guide__highlight">
            {t("unpaidOrders.guideReceipt")}
          </span>
          {t("unpaidOrders.guideNoSuffix")}
        </p>

        <div className="unpaid-guide__order-wrap">
          <div className="order-card">
            <div className="order-card__header">
              <div className="order-card__info">
                <div className="order-card__order-no">
                  {t("unpaidOrders.orderNo")} {order.id}
                </div>
                <div className="order-card__amount">
                  {order.currencySymbol || "$"}
                  {order.amount}
                </div>
              </div>
              <span className="order-card__badge order-card__badge--pending">
                {t("unpaidOrders.statusUnpaid")}
              </span>
            </div>
            <div className="order-card__created">
              {t("unpaidOrders.created")}: {formatCreatedDate(order.created)}
            </div>
            <div className="order-card__body" style={{ display: "block" }}>
              <Suspense fallback={null}>
                <UploadProofSectionB
                  orderId={order.id}
                  initialVoucherImgs={order.paymentVoucherImgs}
                  isProcessing={false}
                  rejectTemplate={
                    isRejected ? order.rejectTemplateContent : undefined
                  }
                  onUploadSuccess={handleUploadSuccess}
                />
              </Suspense>
            </div>
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default UnpaidGuideModal;
